import React, { useCallback, useState } from 'react';
import {
  Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  ChevronRightRounded as ChevronIcon, FolderOutlined as FolderIcon, PeopleAltOutlined as PersonIcon,
  LocalShippingOutlined as TruckIcon, ReportProblemOutlined as CaseIcon, ViewListRounded as ListIcon,
  GridViewRounded as GridIcon, PictureAsPdfOutlined as PdfIcon, ImageOutlined as ImageIcon,
  TableChartOutlined as SheetIcon, DescriptionOutlined as DocIcon, MovieOutlined as VideoIcon,
  AudiotrackOutlined as AudioIcon, InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material';
import type { DocumentOwnerType, TopOpsDocument } from '../../api/services/document.service';
import AttentionLine, { Attention, attentionUrgent } from './AttentionLine';
import { previewKind } from './FilePreview';
import { describeExpiry, toneForDocument, colorForTone } from './vaultStatus';

// Browsing Files the way Drive does: folders first, then files, as a list
// or as a grid of tiles — one choice, remembered, the same on every level
// of the tree. What a folder is (a subfolder, a person, a truck, a case)
// only changes its icon.

export type ViewMode = 'list' | 'grid';
const VIEW_KEY = 'files.view';

export function useViewMode(): [ViewMode, (mode: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list'; } catch { return 'list'; }
  });
  const set = useCallback((next: ViewMode) => {
    setMode(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* a private window keeps it for the visit */ }
  }, []);
  return [mode, set];
}

export const ViewModeToggle: React.FC<{ mode: ViewMode; onChange: (mode: ViewMode) => void }> = ({ mode, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && onChange(v)}>
    <ToggleButton value="list" aria-label="List"><Tooltip title="List"><ListIcon fontSize="small" /></Tooltip></ToggleButton>
    <ToggleButton value="grid" aria-label="Grid"><Tooltip title="Grid"><GridIcon fontSize="small" /></Tooltip></ToggleButton>
  </ToggleButtonGroup>
);

export interface FolderItem {
  key: string;
  name: string;
  sublabel?: string | null;
  count: number;
  attention?: Attention;
  /** People, trucks and cases get their own icon; anything else is a folder. */
  ownerType?: DocumentOwnerType | null;
  muted?: boolean;
  onOpen: () => void;
}

const iconFor = (ownerType?: DocumentOwnerType | null, size = 22) => {
  const sx = { fontSize: size, color: 'text.secondary' };
  if (ownerType === 'driver') return <PersonIcon sx={sx} />;
  if (ownerType === 'vehicle') return <TruckIcon sx={sx} />;
  if (ownerType === 'accident') return <CaseIcon sx={sx} />;
  return <FolderIcon sx={sx} />;
};

const filesWord = (n: number) => `${n} ${n === 1 ? 'file' : 'files'}`;

const activate = (open: () => void) => ({
  role: 'link',
  tabIndex: 0,
  onClick: open,
  onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') open(); },
});

/** Folders as rows (list) or tiles (grid). */
export const FolderItems: React.FC<{ items: FolderItem[]; mode: ViewMode }> = ({ items, mode }) => {
  if (!items.length) return null;
  if (mode === 'grid') {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5 }}>
        {items.map((item) => (
          <Box
            key={item.key}
            {...activate(item.onOpen)}
            sx={{
              p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
              cursor: 'pointer', opacity: item.muted ? 0.6 : 1, minWidth: 0,
              display: 'flex', alignItems: 'center', gap: 1.25, '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {iconFor(item.ownerType, 26)}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{item.name}</Typography>
              {attentionUrgent(item.attention) || item.attention?.expiring
                ? <AttentionLine attention={item.attention} />
                : <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{item.sublabel || filesWord(item.count)}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
      {items.map((item, index) => (
        <Box
          key={item.key}
          {...activate(item.onOpen)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer', minHeight: 56,
            borderTop: index ? '1px solid' : 'none', borderColor: 'divider', opacity: item.muted ? 0.6 : 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {iconFor(item.ownerType)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{item.name}</Typography>
            {item.sublabel && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{item.sublabel}</Typography>}
            <AttentionLine attention={item.attention} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{filesWord(item.count)}</Typography>
          <ChevronIcon sx={{ color: 'text.disabled' }} />
        </Box>
      ))}
    </Box>
  );
};

const fileIcon = (document: TopOpsDocument) => {
  const kind = previewKind(document.file?.contentType, document.file?.path);
  const sx = { fontSize: 34 };
  if (kind === 'pdf') return <PdfIcon sx={{ ...sx, color: 'error.main' }} />;
  if (kind === 'image') return <ImageIcon sx={{ ...sx, color: 'info.main' }} />;
  if (kind === 'csv' || kind === 'tsv' || kind === 'xlsx') return <SheetIcon sx={{ ...sx, color: 'success.main' }} />;
  if (kind === 'docx' || kind === 'text' || kind === 'json') return <DocIcon sx={{ ...sx, color: 'primary.main' }} />;
  if (kind === 'video') return <VideoIcon sx={{ ...sx, color: 'secondary.main' }} />;
  if (kind === 'audio') return <AudioIcon sx={{ ...sx, color: 'secondary.main' }} />;
  return <FileIcon sx={{ ...sx, color: 'text.secondary' }} />;
};

/**
 * Files as tiles. Click to inspect, double-click to open — the same as the
 * list, so switching views changes nothing but the shape.
 */
export const FileGrid: React.FC<{
  documents: TopOpsDocument[];
  selectedId?: string;
  onSelect: (document: TopOpsDocument) => void;
  onOpen: (document: TopOpsDocument) => void;
}> = ({ documents, selectedId, onSelect, onOpen }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5 }}>
    {documents.map((document) => {
      const selected = document._id === selectedId;
      const expiry = document.status === 'expired' || document.status === 'expiring_soon'
        ? describeExpiry(document.expiresAt) : null;
      return (
        <Box
          key={document._id}
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          onClick={() => onSelect(document)}
          onDoubleClick={() => onOpen(document)}
          onKeyDown={(e) => { if (e.key === 'Enter') onOpen(document); }}
          sx={{
            borderRadius: 3, border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'primary.light' : 'background.paper', cursor: 'pointer', overflow: 'hidden', minWidth: 0,
            '&:hover': { borderColor: selected ? 'primary.main' : 'grey.400' },
          }}
        >
          <Box sx={{ height: 96, display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>{fileIcon(document)}</Box>
          <Box sx={{ px: 1.25, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={document.fileName}>{document.fileName}</Typography>
            <Typography
              variant="caption" noWrap sx={{ display: 'block' }}
              color={expiry ? `${colorForTone(toneForDocument(document.status))}.main` : 'text.secondary'}
            >
              {expiry || new Date(document.issuedAt || document.uploadedAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);
